import { useTranslation } from 'react-i18next';
import { z } from 'zod';

const translationRegex = /^[\p{L}\p{N}\s''&\-()/.,!]+$/u;
const directionTipRegex = /^[\p{L}\p{N}\p{So}\p{Sk}\s.,!?()_*-]+$/u;

export const useCreateDestinationValidationSchema = () => {
  const { t } = useTranslation(['validation']);

  return z.object({
    name: z
      .string()
      .min(1, t('required'))
      .max(
        255,
        t('maxCharacters', {
          count: 255,
        }),
      )
      .regex(/^[\p{L}\p{N} &-]+$/u, t('invalidFormat')),

    floor: z
      .string()
      .min(1, t('required'))
      .max(
        255,
        t('maxCharacters', {
          count: 255,
        }),
      )
      .regex(/^[\p{L}\p{N}\p{So}\p{Sk}\s.,!?()_*-]+$/u, t('invalidFormat')),

    maxCapacity: z.coerce
      .number({
        invalid_type_error: t('required'),
      })
      .min(
        1,
        t('minNumber', {
          number: 1,
        }),
      ),

    description: z
      .string()
      .min(1, t('required'))
      .max(
        1024,
        t('maxCharacters', {
          count: 1024,
        }),
      ),

    accessBeforeVisitInMinutes: z.coerce
      .number({
        invalid_type_error: t('required'),
      })
      .min(
        0,
        t('minNumber', {
          number: 0,
        }),
      ),
    accessAfterVisitInMinutes: z.coerce
      .number({
        invalid_type_error: t('required'),
      })
      .min(
        0,
        t('minNumber', {
          number: 0,
        }),
      ),
    translations: z
      .object({
        pl: z
          .string()
          .refine(val => !val || val.length >= 1, {
            message: t('required'),
          })
          .refine(val => !val || val.length <= 255, {
            message: t('maxCharacters', { count: 255 }),
          })
          .refine(val => !val || translationRegex.test(val), {
            message: t('invalidFormat'),
          })
          .optional(),
        en: z
          .string()
          .refine(val => !val || val.length >= 1, {
            message: t('required'),
          })
          .refine(val => !val || val.length <= 255, {
            message: t('maxCharacters', { count: 255 }),
          })
          .refine(val => !val || translationRegex.test(val), {
            message: t('invalidFormat'),
          })
          .optional(),
        ua: z
          .string()
          .refine(val => !val || val.length >= 1, {
            message: t('required'),
          })
          .refine(val => !val || val.length <= 255, {
            message: t('maxCharacters', { count: 255 }),
          })
          .refine(val => !val || translationRegex.test(val), {
            message: t('invalidFormat'),
          })
          .optional(),
      })
      .strict(),

    directionTips: z
      .array(
        z
          .object({
            pl: z
              .string()
              .refine(val => !val || val.length <= 255, {
                message: t('maxCharacters', { count: 255 }),
              })
              .refine(val => !val || directionTipRegex.test(val), {
                message: t('invalidFormat'),
              }),
            en: z
              .string()
              .refine(val => !val || val.length <= 255, {
                message: t('maxCharacters', { count: 255 }),
              })
              .refine(val => !val || directionTipRegex.test(val), {
                message: t('invalidFormat'),
              }),
            ua: z
              .string()
              .refine(val => !val || val.length <= 255, {
                message: t('maxCharacters', { count: 255 }),
              })
              .refine(val => !val || directionTipRegex.test(val), {
                message: t('invalidFormat'),
              }),
          })
          .strict(),
      )
      .max(
        50,
        t('maxNumber', {
          number: 50,
        }),
      ),

    hosts: z
      .array(
        z.object({
          id: z.number(),
          name: z.string(),
        }),
      )
      .optional(),

    devices: z
      .array(
        z.object({
          id: z.number(),
          name: z.string(),
        }),
      )
      .optional(),

    allowedKiosks: z.array(
      z.object({
        id: z.number(),
        name: z.string(),
      }),
    ),
    defaultHostId: z.number().nullable(),
    addHostStepEnabled: z.boolean(),
    hostObligatory: z.boolean(),
    hostConfirmationObligatory: z.boolean(),
    hostConfirmationInEmail: z.boolean(),
    deviceConfirmation: z.boolean(),
    kioskVisitHours: z.coerce
      .number({
        invalid_type_error: t('required'),
      })
      .min(
        1,
        t('minNumber', {
          number: 1,
        }),
      ),
  });
};
